/** Login screen — email + password against POST /auth/login. */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import { AuthShell, authButtonClass, authErrorClass, authInputClass } from "@/auth/AuthShell";

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch {
      setError(t("auth.invalidCredentials"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className="space-y-4">
        <h1 className="text-base font-semibold">{t("auth.signIn")}</h1>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-muted">{t("auth.email")}</span>
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInputClass}
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-muted">{t("auth.password")}</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={authInputClass}
          />
        </label>

        {error && (
          <p
            role="alert"
            className={authErrorClass}
            style={{ color: "rgb(var(--color-status-critical))" }}
          >
            {error}
          </p>
        )}

        <button type="submit" disabled={busy} className={authButtonClass}>
          {busy ? t("auth.signingIn") : t("auth.signIn")}
        </button>

        <Link to="/forgot-password" className="block text-center text-sm text-brand hover:underline">
          {t("auth.forgotLink")}
        </Link>
      </form>
    </AuthShell>
  );
}
